import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { sameOrigin } from '@/lib/sameOriginClient';
import { useToast } from '@/hooks/use-toast';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { buyerMarkNotificationRead } from '@/services/edgeFunctions';

export interface BuyerNotification {
  id: string;
  user_id: string;
  deal_id: string | null;
  notification_type: 'viewed' | 'approved' | 'denied' | 'counter' | 'expired';
  title: string;
  message: string;
  dealer_name: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

/**
 * Enterprise-grade hook for managing buyer notifications
 * 
 * CRITICAL: Uses server-authoritative edge function for mark-as-read operations.
 * The edge function:
 * - Verifies ownership server-side
 * - Uses service role key to bypass RLS
 * - Returns explicit ok/error response
 * 
 * Client-side optimistic updates for fast UI response with server reconciliation.
 */
export function useBuyerNotifications(userId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch all notifications for the user
  const notificationsQuery = useQuery({
    queryKey: ['buyer-notifications', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await sameOrigin.select<BuyerNotification>('buyer_notifications', {
        select: '*',
        filters: [{ column: 'user_id', op: 'eq', value: userId }],
        order: { column: 'created_at', ascending: false },
      });

      if (error) throw error;
      return (data || []) as BuyerNotification[];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  // Count unread notifications
  const unreadCount = notificationsQuery.data?.filter((n) => !n.is_read).length || 0;

  // Mark a single notification as read with optimistic update
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      // Get current session for auth token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error('Not authenticated');
      }

      console.log('[useBuyerNotifications] Marking as read via edge function:', notificationId);

      const { data: response, error: fnError } = await buyerMarkNotificationRead(
        { notification_id: notificationId },
        session.access_token
      );

      if (fnError) {
        console.error('[useBuyerNotifications] Edge function error:', fnError);
        throw fnError;
      }

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        console.error('[useBuyerNotifications] Server returned error:', response);
        const errorMessage = 'message' in response ? response.message : 'Failed to mark notification as read';
        throw new Error(errorMessage);
      }

      console.log('[useBuyerNotifications] Mark as read success:', response);
      return notificationId;
    },
    // Optimistic update for immediate badge decrement
    onMutate: async (notificationId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['buyer-notifications', userId] });

      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData<BuyerNotification[]>(['buyer-notifications', userId]);

      // Optimistically update to the new value
      if (previousNotifications) {
        queryClient.setQueryData<BuyerNotification[]>(
          ['buyer-notifications', userId],
          previousNotifications.map((n) =>
            n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
          )
        );
      }

      return { previousNotifications };
    },
    onError: (err, _notificationId, context) => {
      console.error('[useBuyerNotifications] Mark as read failed, rolling back:', err);
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(['buyer-notifications', userId], context.previousNotifications);
      }
      toast({
        title: 'Failed to mark notification as read',
        description: err.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['buyer-notifications', userId] });
      queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });

  // Mark all notifications as read with optimistic update
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!userId) return;

      // Get current session for auth token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error('Not authenticated');
      }

      console.log('[useBuyerNotifications] Marking all as read via edge function');

      const { data: response, error: fnError } = await buyerMarkNotificationRead(
        { mark_all: true },
        session.access_token
      );

      if (fnError) {
        console.error('[useBuyerNotifications] Edge function error:', fnError);
        throw fnError;
      }

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        console.error('[useBuyerNotifications] Server returned error:', response);
        const errorMessage = 'message' in response ? response.message : 'Failed to mark notifications as read';
        throw new Error(errorMessage);
      }

      console.log('[useBuyerNotifications] Mark all as read success:', response);
    },
    // Optimistic update for immediate badge clear
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['buyer-notifications', userId] });

      const previousNotifications = queryClient.getQueryData<BuyerNotification[]>(['buyer-notifications', userId]);

      if (previousNotifications) {
        queryClient.setQueryData<BuyerNotification[]>(
          ['buyer-notifications', userId],
          previousNotifications.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
        );
      }

      return { previousNotifications };
    },
    onError: (err, _vars, context) => {
      console.error('[useBuyerNotifications] Mark all as read failed, rolling back:', err);
      if (context?.previousNotifications) {
        queryClient.setQueryData(['buyer-notifications', userId], context.previousNotifications);
      }
      toast({
        title: 'Failed to mark notifications as read',
        description: err.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['buyer-notifications', userId] });
      queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });

  // Consolidated realtime subscription for both notifications and deals
  useRealtimeSubscription({
    channelName: `buyer-realtime-${userId}`,
    enabled: !!userId,
    tables: [
      {
        table: 'buyer_notifications',
        event: 'INSERT',
        filter: `user_id=eq.${userId}`,
        onData: (payload) => {
          const newNotification = payload.new as unknown as BuyerNotification;
          const toastVariant = getToastVariant(newNotification.notification_type);
          toast({
            title: newNotification.title,
            description: newNotification.message,
            variant: toastVariant,
          });
        },
        invalidateQueries: [['buyer-notifications', userId!]],
      },
      {
        table: 'deals',
        event: 'UPDATE',
        filter: `user_id=eq.${userId}`,
        // Invalidate ALL deal-related caches including bootstrap
        invalidateQueries: [
          ['deals'],
          ['user-deals', userId!],
          ['buyer-deals-lite', userId!],
          ['bootstrap'], // Critical: buyer account page uses bootstrap for deal data
        ],
      },
    ],
  });

  return {
    notifications: notificationsQuery.data || [],
    isLoading: notificationsQuery.isLoading,
    unreadCount,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingRead: markAsReadMutation.isPending,
    isMarkingAllRead: markAllAsReadMutation.isPending,
  };
}

function getToastVariant(type: BuyerNotification['notification_type']): 'default' | 'destructive' {
  switch (type) {
    case 'denied':
    case 'expired':
      return 'destructive';
    default:
      return 'default';
  }
}

/**
 * Get notification icon and color based on type
 */
export function getNotificationStyle(type: BuyerNotification['notification_type']) {
  switch (type) {
    case 'viewed':
      return { icon: 'Eye', color: 'text-blue-500', bgColor: 'bg-blue-500/10' };
    case 'approved':
      return { icon: 'CheckCircle', color: 'text-green-500', bgColor: 'bg-green-500/10' };
    case 'denied':
      return { icon: 'XCircle', color: 'text-destructive', bgColor: 'bg-destructive/10' };
    case 'counter':
      return { icon: 'MessageSquare', color: 'text-amber-500', bgColor: 'bg-amber-500/10' };
    case 'expired':
      return { icon: 'Clock', color: 'text-muted-foreground', bgColor: 'bg-muted' };
    default:
      return { icon: 'Bell', color: 'text-foreground', bgColor: 'bg-muted' };
  }
}
